import { SectionLabel, RevealLines, FadeUp } from "../site/motion";

const H2 = "font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-cream sm:text-5xl lg:text-6xl";
const Wrap = ({ children, testid, className = "" }) => (
  <section data-testid={testid} className={`relative py-24 md:py-32 ${className}`}>
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">{children}</div>
  </section>
);

export const StatsBand = ({ stats }) => (
  <section data-testid="case-stats" className="border-y border-cream/10">
    <div className="mx-auto grid max-w-[1440px] grid-cols-2 md:grid-cols-4">
      {stats.map((s, i) => (
        <FadeUp key={s.l} delay={i * 0.07} className={`px-6 py-10 md:px-10 md:py-14 ${i % 2 ? "border-l" : ""} ${i > 1 ? "border-t md:border-t-0" : ""} ${i === 2 ? "md:border-l" : ""} border-cream/10`}>
          <p className="font-display text-4xl font-semibold tracking-tight text-cream md:text-6xl">{s.v}</p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">{s.l}</p>
        </FadeUp>
      ))}
    </div>
  </section>
);

export const Problem = ({ p }) => (
  <Wrap testid="case-problem">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionLabel n="(01)">Problem statement</SectionLabel></div>
      <div className="lg:col-span-8">
        <RevealLines inView as="h2" lines={p.title} className={H2} lineClass={(i) => (i === 1 ? "case-text" : "")} />
        <div className="mt-10 grid gap-6 font-body text-base leading-relaxed text-cream/75 md:grid-cols-2">
          {p.body.map((b, i) => <FadeUp key={i} delay={i * 0.1}><p>{b}</p></FadeUp>)}
        </div>
      </div>
    </div>
    <p className="mt-20 font-mono text-[11px] uppercase tracking-[0.25em] text-cream/55">{p.cardsLabel}</p>
    <div className="mt-6 grid gap-4 md:grid-cols-3">
      {p.cards.map((c, i) => (
        <FadeUp key={c.t} delay={i * 0.08} data-testid={`case-problem-card-${i}`} className="group relative overflow-hidden rounded-[22px] border border-cream/10 bg-skaris-card p-7 transition-colors duration-500 hover:border-[color:var(--case)]">
          <span className="font-mono text-xs" style={{ color: "var(--case)" }}>0{i + 1}</span>
          <h3 className="mt-10 font-display text-2xl font-semibold text-cream">{c.t}</h3>
          <p className="mt-3 font-body text-sm leading-relaxed text-cream/75">{c.b}</p>
        </FadeUp>
      ))}
    </div>
    {p.aside && (
      <FadeUp className="mt-4 flex flex-col gap-3 rounded-[22px] border border-dashed border-cream/20 p-7 md:flex-row md:items-center md:gap-10">
        <p className="shrink-0 font-display text-xl font-semibold text-cream">{p.aside.t}</p>
        <p className="font-body text-sm text-cream/75">{p.aside.b}</p>
      </FadeUp>
    )}
  </Wrap>
);

const Table = ({ rows, note }) => (
  <FadeUp className="mt-12 overflow-x-auto rounded-[22px] border border-cream/10">
    <table data-testid="case-competitor-table" className="w-full min-w-[640px] text-left">
      <thead className="bg-skaris-navy/40 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">
        <tr><th className="px-6 py-4 font-normal">Competitor</th><th className="px-6 py-4 font-normal">Market</th><th className="px-6 py-4 font-normal">What it offers</th></tr>
      </thead>
      <tbody className="font-body text-sm">
        {rows.map((r) => (
          <tr key={r[0]} className="border-t border-cream/10 transition-colors hover:bg-cream/[0.03]">
            <td className="px-6 py-4 font-display text-base font-semibold text-cream">{r[0]}</td>
            <td className="px-6 py-4 text-cream/70">{r[1]}</td>
            <td className="px-6 py-4 text-cream/75">{r[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {note && <p className="border-t border-cream/10 px-6 py-3 font-body text-xs text-cream/55">{note}</p>}
  </FadeUp>
);

export const Research = ({ r }) => (
  <Wrap testid="case-research" className="border-t border-cream/10">
    <SectionLabel n="(02)">{r.label}</SectionLabel>
    <FadeUp as="h2" className="mt-6 max-w-5xl font-display text-3xl font-medium leading-[1.12] tracking-[-0.02em] text-cream md:text-5xl">{r.title}</FadeUp>
    {r.intro && <FadeUp as="p" className="mt-8 max-w-3xl font-body text-base leading-relaxed text-cream/75">{r.intro}</FadeUp>}
    {r.table && <Table rows={r.table} note={r.note} />}
    <div className={`mt-12 grid gap-px overflow-hidden rounded-[22px] border border-cream/10 bg-cream/10 ${r.items.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
      {r.items.map((it, i) => (
        <FadeUp key={it.t} delay={i * 0.07} className="bg-skaris-ink p-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--case)" }}>{it.t}</p>
          <p className="mt-4 font-display text-lg leading-snug text-cream">{it.b}</p>
        </FadeUp>
      ))}
    </div>
  </Wrap>
);

const Flow = ({ steps, note }) => (
  <div className="mt-14">
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
      {steps.map((s, i) => (
        <FadeUp key={s.t} delay={i * 0.08} data-testid={`case-flow-${i}`} className="relative rounded-[18px] border border-cream/15 bg-skaris-card p-5">
          <span className="font-mono text-[10px] text-cream/50">{String(i + 1).padStart(2, "0")}</span>
          <p className="mt-6 font-display text-lg font-semibold text-cream">{s.t}</p>
          <p className="mt-2 font-body text-[13px] leading-snug text-cream/70">{s.b}</p>
          {i < steps.length - 1 && <span className="absolute -right-2.5 top-1/2 z-10 hidden h-px w-4 lg:block" style={{ background: "var(--case)" }} />}
        </FadeUp>
      ))}
    </div>
    {note && <p className="mt-5 font-body text-sm text-cream/65">{note}</p>}
  </div>
);

export const Approach = ({ a }) => (
  <Wrap testid="case-approach" className="border-t border-cream/10">
    <SectionLabel n="(03)">Our approach</SectionLabel>
    <FadeUp as="h2" className="mt-6 max-w-5xl font-display text-3xl font-medium leading-[1.12] tracking-[-0.02em] text-cream md:text-5xl">{a.title}</FadeUp>
    {a.flow && <Flow steps={a.flow} note={a.flowNote} />}
    <div className="mt-14 grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
      {a.items.map((it, i) => (
        <FadeUp key={it.t} delay={(i % 3) * 0.08} className="border-t border-cream/15 pt-6">
          <span className="font-mono text-[11px] text-cream/50">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="mt-3 font-display text-2xl font-semibold text-cream">{it.t}</h3>
          <p className="mt-3 font-body text-sm leading-relaxed text-cream/75">{it.b}</p>
        </FadeUp>
      ))}
    </div>
  </Wrap>
);

export const DesignSystem = ({ s }) => (
  <Wrap testid="case-design-system" className="border-t border-cream/10">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionLabel>Design system</SectionLabel>
        <FadeUp as="h2" className="mt-6 font-display text-3xl font-semibold leading-tight text-cream md:text-5xl">{s.title}</FadeUp>
        <FadeUp as="p" className="mt-6 font-body text-base leading-relaxed text-cream/75">{s.body}</FadeUp>
      </div>
      <div className="lg:col-span-7">
        <div className="flex h-56 overflow-hidden rounded-[22px] md:h-72">
          {s.swatches.map((sw, i) => (
            <FadeUp key={sw.h} delay={i * 0.06} y={60} className="group relative flex-1 transition-[flex-grow] duration-700 hover:flex-[2]" style={{ background: sw.h }}>
              <span className="absolute bottom-4 left-4 hidden font-mono text-[10px] uppercase tracking-widest sm:block" style={{ color: i === 0 || i === 2 ? "#F6F6FF" : "#262626" }}>{sw.n}<br />{sw.h}</span>
            </FadeUp>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          {s.specs.map((sp) => (
            <div key={sp.t} className="rounded-[16px] border border-cream/10 p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/55">{sp.t}</p>
              <p className="mt-2 font-body text-sm text-cream/85">{sp.b}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Wrap>
);

export const Hard = ({ h }) => (
  <Wrap testid="case-hard-problems" className="bg-cream text-skaris-ink">
    <SectionLabel n="(05)" className="!text-skaris-accent [&_span:first-child]:!bg-skaris-accent [&_span:nth-child(2)]:!text-skaris-ink/60">Difficult problems we solved</SectionLabel>
    <FadeUp as="h2" className="mt-6 max-w-5xl font-display text-3xl font-semibold leading-[1.1] tracking-[-0.02em] text-skaris-navy md:text-5xl">{h.title}</FadeUp>
    {h.lead && <FadeUp as="p" className="mt-6 max-w-3xl font-body text-lg leading-relaxed text-skaris-ink/80">{h.lead}</FadeUp>}
    {h.flow && (
      <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {h.flow.map((f, i) => (
          <FadeUp key={f.t} delay={i * 0.08} className={`rounded-[20px] p-6 ${i === 3 ? "border border-skaris-navy/25" : "bg-skaris-navy text-cream"}`}>
            <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${i === 3 ? "text-skaris-accent" : "text-skaris-glow"}`}>{f.t}</p>
            <p className={`mt-6 font-body text-sm leading-relaxed ${i === 3 ? "text-skaris-ink/80" : "text-cream/85"}`}>{f.b}</p>
          </FadeUp>
        ))}
      </div>
    )}
    {h.img && (
      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <FadeUp className="lg:col-span-7">
          <div className="overflow-hidden rounded-[22px] bg-black"><img src={h.img} alt={h.imgCaption} className="w-full" loading="lazy" /></div>
          <p className="mt-3 font-body text-xs text-skaris-ink/70">{h.imgCaption}</p>
        </FadeUp>
        <div className="space-y-8 lg:col-span-5">
          {h.items.map((it, i) => (
            <FadeUp key={it.t} delay={i * 0.08} className="border-t border-skaris-navy/20 pt-5">
              <h3 className="font-display text-xl font-semibold text-skaris-navy">{it.t}</h3>
              <p className="mt-2 font-body text-sm leading-relaxed text-skaris-ink/80">{it.b}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    )}
    {!h.img && (
    <div className="mt-14 grid gap-10 md:grid-cols-3">
      {h.items.map((it, i) => (
        <FadeUp key={it.t} delay={i * 0.08} className="border-t border-skaris-navy/20 pt-6">
          <h3 className="font-display text-xl font-semibold text-skaris-navy">{it.t}</h3>
          <p className="mt-3 font-body text-sm leading-relaxed text-skaris-ink/80">{it.b}</p>
        </FadeUp>
      ))}
    </div>
    )}
  </Wrap>
);

export const Results = ({ r }) => (
  <Wrap testid="case-results">
    <SectionLabel n="(06)">Result</SectionLabel>
    <RevealLines inView as="h2" lines={r.title} className={`mt-6 ${H2}`} lineClass={(i) => (i === 1 ? "case-text" : "")} />
    {r.note && <FadeUp as="p" className="mt-6 max-w-2xl font-body text-base text-cream/75">{r.note}</FadeUp>}
    <div className={`mt-14 grid gap-px overflow-hidden rounded-[22px] border border-cream/10 bg-cream/10 sm:grid-cols-2 ${r.stats.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
      {r.stats.map((s, i) => (
        <FadeUp key={s.l} delay={i * 0.07} data-testid={`case-result-${i}`} className="bg-skaris-ink p-7">
          <p className="font-display text-5xl font-semibold tracking-tight text-cream">{s.v}</p>
          <p className="mt-3 font-body text-sm leading-snug text-cream/70">{s.l}</p>
        </FadeUp>
      ))}
    </div>
    {r.roadmap && (
      <FadeUp className="mt-6 rounded-[22px] border border-dashed border-cream/20 p-7">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--case)" }}>Next on the roadmap</p>
        <p className="mt-3 font-display text-xl text-cream">{r.roadmap}</p>
      </FadeUp>
    )}
  </Wrap>
);
